import React from 'react';
import { Card, Badge } from '@parsina/framework';
import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { conditionStatusKey } from '../../lib/patients.js';
import { MobileSectionTitle } from './MobileKit.jsx';

// سوابق — what has been recorded about this patient, read-only.
//
// The desktop row is one flex line whose date is pushed to the far end with
// `marginInlineStart: auto`; at 320px that collides with a long condition name.
// Here every entry is its own card: the name on the first line, the code, the
// status and the date beneath it, so nothing has to fight for the same line.
//
// Conditions and medications come from clinicalencounter and are keyed on
// `appointment_id`: there is no encounters table on this platform, encounters
// ARE appointments. Nothing here groups by "visit" for that reason — inventing
// a visit grouping the data does not carry would be inventing a fact.

const dosageLine = (medication) => {
  const d = medication?.dosageInstruction;
  if (!d) return '';
  if (typeof d === 'string') return d;
  if (d.text) return d.text;
  return [d.dose, d.frequency, d.duration].filter(Boolean).join(' · ');
};

const Empty = ({ children }) => (
  <Card style={{ padding: '0.875rem' }}>
    <div style={{ fontSize: '0.8125rem', color: C.textMuted }}>{children}</div>
  </Card>
);

const MobilePatientHistory = ({ patient }) => {
  const { t, formatDate } = useTranslation();
  const conditions = patient.conditions || [];
  const medications = patient.medications || [];

  return (
    <div data-testid="patient-history">
      <MobileSectionTitle>{t('patient.history.conditions')}</MobileSectionTitle>
      {conditions.length === 0 ? (
        <Empty>{t('patient.history.noConditions')}</Empty>
      ) : (
        conditions.map((c) => (
          <Card
            key={c.id}
            style={{ padding: '0.75rem', marginBottom: '0.5rem', borderRadius: '0.875rem' }}
          >
            <div dir="auto" style={{ fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary }}>
              {c.display}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                flexWrap: 'wrap',
                marginTop: '0.375rem',
              }}
            >
              {c.code && (
                <span dir="ltr" style={{ unicodeBidi: 'isolate', fontSize: '0.6875rem', color: C.textMuted }}>
                  {c.code}
                </span>
              )}
              {/* Recognised FHIR status → label; unknown → verbatim. */}
              {c.clinicalStatus && (
                <Badge variant="outline">
                  {conditionStatusKey(c.clinicalStatus)
                    ? t(conditionStatusKey(c.clinicalStatus))
                    : c.clinicalStatus}
                </Badge>
              )}
              {c.onsetDate && (
                <span style={{ fontSize: '0.6875rem', color: C.textMuted, marginInlineStart: 'auto' }}>
                  {formatDate(c.onsetDate, 'numeric')}
                </span>
              )}
            </div>
          </Card>
        ))
      )}

      <MobileSectionTitle style={{ marginTop: '0.875rem' }}>
        {t('patient.history.medications')}
      </MobileSectionTitle>
      {medications.length === 0 ? (
        <Empty>{t('patient.history.noMedications')}</Empty>
      ) : (
        medications.map((m) => (
          <Card
            key={m.id}
            style={{ padding: '0.75rem', marginBottom: '0.5rem', borderRadius: '0.875rem' }}
          >
            <div dir="auto" style={{ fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary }}>
              {m.display}
            </div>
            {dosageLine(m) && (
              <div dir="auto" style={{ fontSize: '0.75rem', color: C.textSecondary, marginTop: '0.25rem' }}>
                {dosageLine(m)}
              </div>
            )}
            {m.authoredOn && (
              <div style={{ fontSize: '0.6875rem', color: C.textMuted, marginTop: '0.25rem' }}>
                {t('patient.history.prescribedOn', { date: formatDate(m.authoredOn, 'numeric') })}
              </div>
            )}
          </Card>
        ))
      )}
    </div>
  );
};

export default MobilePatientHistory;
